import { selectTranslation } from '@store/slices/account/accountSlice.js';
import { getToken } from '@utils/tokenStorage.js';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import * as S from './updatePrompt.styled';

// Prod bundles are content-hashed and Core's React.cshtml is repointed at the fresh names by every
// deploy, so the hash of the main chunk IS the deployed version — no build id or extra artifact to
// keep in sync with three different deploy scripts.
const MAIN_CHUNK_RE = /\/build\/main\.([a-f0-9]{8,})\.js/;

// `/website` is that Razor view: ~3 KB, `Cache-Control: private`, never edge-cached — the cheapest
// honest answer to "which build is live right now?".
const VERSION_PROBE_URL = '/website';

const POLL_MS = 5 * 60 * 1000;
// A tab coming back into focus re-checks immediately, but not more often than this.
const FOCUS_MIN_GAP_MS = 60 * 1000;

const DISMISSED_KEY = 'updatePromptDismissedVersion';

// The version this tab is running, read off the <script> tag that loaded it. Dev (unhashed
// `main.js`) and the Capacitor build (assets served from the app root, not `/build/`) have nothing
// to match, so the check stays off there — which is what we want in both cases.
const readLoadedVersion = (): string | null => {
  for (const script of Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))) {
    const match = script.src.match(MAIN_CHUNK_RE);
    if (match?.[1]) return match[1];
  }

  return null;
};

const fetchDeployedVersion = async (): Promise<string | null> => {
  const response = await fetch(VERSION_PROBE_URL, { cache: 'no-store', credentials: 'omit' });
  if (!response.ok) return null;

  return (await response.text()).match(MAIN_CHUNK_RE)?.[1] ?? null;
};

// localStorage throws in private mode / with storage blocked, and a nag prompt is not worth a crash.
const readDismissed = (): string | null => {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
};

const writeDismissed = (version: string) => {
  try {
    localStorage.setItem(DISMISSED_KEY, version);
  } catch {
    // nothing to do — the prompt just comes back on the next check
  }
};

type Translation = { updatePrompt?: { title?: string; message?: string; action?: string } };

export const UpdatePrompt = () => {
  const translation: Translation = useSelector(selectTranslation);

  const [newVersion, setNewVersion] = useState<string | null>(null);

  useEffect(() => {
    const loadedVersion = readLoadedVersion();
    if (!loadedVersion) return undefined;

    let isCancelled = false;
    let checkedAt = 0;

    const check = async () => {
      // Hidden tabs and the login screen have nobody to prompt.
      if (document.hidden || !getToken()) return;

      checkedAt = Date.now();

      try {
        const deployedVersion = await fetchDeployedVersion();
        if (isCancelled || !deployedVersion || deployedVersion === loadedVersion) return;
        if (readDismissed() === deployedVersion) return;

        setNewVersion(deployedVersion);
      } catch {
        // offline, or Core hiccuped — the next tick asks again
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && Date.now() - checkedAt > FOCUS_MIN_GAP_MS) void check();
    };

    const timer = window.setInterval(() => void check(), POLL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isCancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (!newVersion) return null;

  const handleDismiss = () => {
    writeDismissed(newVersion);
    setNewVersion(null);
  };

  return (
    <S.Prompt
      role='status'
      aria-live='polite'
      initial={{ opacity: 0, y: -16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
    >
      <S.Close type='button' aria-label='Close' onClick={handleDismiss}>
        <X />
      </S.Close>
      <S.Body>
        <S.Text>
          <S.Title>{translation?.updatePrompt?.title ?? 'Update ready'}</S.Title>
          <S.Message>{translation?.updatePrompt?.message ?? 'Reload to use the latest version. Unsaved work on this screen may be lost.'}</S.Message>
        </S.Text>
        <S.UpdateButton type='button' onClick={() => window.location.reload()}>
          {translation?.updatePrompt?.action ?? 'Reload now'}
        </S.UpdateButton>
      </S.Body>
    </S.Prompt>
  );
};
